import { type FC } from "react";
import { Label } from "@root/shared/components/Label";
import { colors, fontSize } from "@root/shared/styles/sprinkles.css";
import { Icon } from "@root/shared/components/Icon";
import { GuidanceBox } from "@root/shared/components/GuidanceBox";
import { ClientShopOrPrefectureSelector } from "../ClientShopOrPrefectureSelector";
import { ClientShopSelector } from "../ClientShopSelector";
import { SelectedShops } from "../SelectedShops";
import { type User, UserTable } from "../UserTable";
import { type AutoSelectProps, WorkSelector } from "../WorkSelector";
import { SpUserCassette } from "./uiParts/SpUserCassette";
import * as styles from "./styles.css";
import { type Work } from "../WorkSelector/useWorksFilter";

type Props = {
  // 表示制御
  isSp: boolean;
  isUserSelectionBundled: boolean;
  isPremiumScout: boolean;
  isDefaultWorkSelected: boolean;
  isPrefectureSelectorVisible: boolean;
  hasMatchingJobs: boolean;

  // データ取得・選択関連
  fetchUserCassetteUrl: string;
  fetchUserCassetteParams: Record<string, unknown>;

  // 求職者関連
  selectedUserIds: number[];
  users: User[];

  // 求人関連
  works: readonly Work[];
  totalWorksCount: number;
  currentWorkId?: string;
  isWorkSelectionRequired: boolean;
  isAutoSelectorVisible: boolean;
  autoSelectDisabledMessage?: string;
  autoSelect: AutoSelectProps;

  // スカウト制限・ガイダンス
  scoutLimitDate: number;

  // 店舗・都道府県関連
  shopOfSelectedWork: readonly {
    name: string;
    prefecture: string;
  }[];
  clientShopId: number | undefined;
  clientShopOptions: readonly {
    value: number;
    label: string;
  }[];
  prefectureId: number | undefined;
  prefectureOptions: readonly {
    value: number;
    label: string;
  }[];
  shopLengthOfPrefecture: number;

  // イベントハンドラ
  onSelectedUserChange: (values: number[]) => void;
  onWorkChange: (workId: string | undefined) => void;
  onClientShopChange: (clientShopId: number | undefined) => void;
  onPrefectureChange: (prefecture: number | undefined) => void;
};

export const Presentation: FC<Props> = ({
  isSp,
  isUserSelectionBundled,
  isPremiumScout,
  isDefaultWorkSelected,
  isPrefectureSelectorVisible,
  hasMatchingJobs,
  fetchUserCassetteUrl,
  fetchUserCassetteParams,
  selectedUserIds,
  users,
  works,
  totalWorksCount,
  currentWorkId,
  isWorkSelectionRequired,
  isAutoSelectorVisible,
  autoSelectDisabledMessage,
  autoSelect,
  scoutLimitDate,
  shopOfSelectedWork,
  clientShopId,
  clientShopOptions,
  prefectureId,
  prefectureOptions,
  shopLengthOfPrefecture,
  onSelectedUserChange,
  onWorkChange,
  onClientShopChange,
  onPrefectureChange,
}) => (
  <>
    {isSp ? (
      <div className={styles.userOuterSp}>
        <SpUserCassette
          fetchUrl={fetchUserCassetteUrl}
          fetchParams={fetchUserCassetteParams}
          selectedUsers={selectedUserIds}
          isBundled={isUserSelectionBundled}
          onSelectedUserChange={onSelectedUserChange}
        />
      </div>
    ) : (
      <UserTable
        selectedUserIds={selectedUserIds}
        users={users}
        isBundled={isUserSelectionBundled}
        onSelectedUserChange={onSelectedUserChange}
      />
    )}
    {works.length > 0 && (
      <section className={styles.selectorArea}>
        {/* NOTE: .js-after-auto-select-scroll-positionは、JSで自動選択後にスクロール位置を調整するためのフック用クラス名です。誤って削除してしまわないよう気をつけてください。 */}
        <h2 className={`${styles.headingLarge} js-after-auto-select-scroll-position`}>
          スカウトする求人
        </h2>
        <div className={styles.selectorOuter}>
          <WorkSelector
            isSp={isSp}
            isRequired={isWorkSelectionRequired}
            totalWorksCount={totalWorksCount}
            currentWorkId={currentWorkId}
            onWorkChange={onWorkChange}
            works={works}
            autoSelectDisabledMessage={autoSelectDisabledMessage}
            isAutoSelectorVisible={isAutoSelectorVisible}
            autoSelect={autoSelect}
            hasMatchingJobs={hasMatchingJobs}
          />
          <GuidanceBox themeColor="gray" className={styles.workSelectGuidance}>
            {!isPremiumScout && (
              <p className={styles.workSelectGuidanceText}>
                ※求職者がキープ中の求人で送る場合は、スカウト残数は減りません。
              </p>
            )}
            {isDefaultWorkSelected ? (
              <p className={styles.workSelectGuidanceText}>
                ※前の画面で選択した求人が反映されています。
              </p>
            ) : (
              <p className={styles.workSelectGuidanceText}>
                ※既に応募された求人と、{scoutLimitDate}
                日以内にスカウトした求人（同条件の求人を含む）は、選択肢に表示されません。
              </p>
            )}
          </GuidanceBox>
        </div>
        {currentWorkId !== "auto" && (
          <>
            <h3 className={styles.headingSmall}>
              <span className={styles.headingSmallInner}>勤務地</span>
              {isPrefectureSelectorVisible ? (
                <Label theme="lightGray">任意</Label>
              ) : (
                <Label theme={isSp ? "red" : "deepPink"} bold={!isSp}>
                  必須
                </Label>
              )}
            </h3>
            {shopOfSelectedWork.length > 0 && (
              <div className={styles.shopOfSelectedWorkOuter}>
                <p className={styles.shopHeadingOuter}>
                  <Icon
                    name="shop"
                    fontSize={fontSize["fs-16"]}
                    color={colors.TextMedium}
                    className={styles.shopIcon}
                  />
                  <span className={styles.shopHeadingInner}>選択した求人の店舗</span>
                  <span className={styles.shopCount}>{shopOfSelectedWork.length}</span>店舗
                </p>
                <div className={styles.SelectedShopsOuter}>
                  <SelectedShops
                    shopNames={shopOfSelectedWork?.map(({ name }) => name) || []}
                    isSp={isSp}
                  />
                </div>
              </div>
            )}
            <div className={styles.shopSelectorOuter}>
              {isPrefectureSelectorVisible ? (
                <ClientShopOrPrefectureSelector
                  clientShopId={clientShopId}
                  onClientShopChange={onClientShopChange}
                  clientShops={clientShopOptions}
                  prefectureId={prefectureId}
                  onPrefectureChange={onPrefectureChange}
                  prefectures={prefectureOptions}
                  shopLengthOfPrefecture={shopLengthOfPrefecture}
                />
              ) : (
                <ClientShopSelector
                  clientShopId={clientShopId}
                  onClientShopChange={onClientShopChange}
                  clientShops={clientShopOptions}
                />
              )}
            </div>
          </>
        )}
      </section>
    )}
  </>
);
